// react
import { useState, useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from 'router';

// components
import { Toast } from '@karehero/llama/components/atoms/Toast/Toast';

// hooks
import { useAuth } from 'hooks';

// store
import { useSelector, dispatch } from 'store';
import {
  getToastQueue,
  newToast,
  removeToast,
  ToastQueueItem,
} from 'store/slices/uiSlice';
import { useSignOutMutation } from 'store/api/iam';
import { getAccount } from 'store/slices/iamSlice';
import { usePushNotification } from 'hooks/usePushNotification';
import useIntercom from 'hooks/useIntercom';
import useUniversalLinkHandler from 'hooks/useUniversalLinkHandler';

/**
 * RootLayout provides the UniversalLinkHandler component with an Outlet.
 */
export const RootLayout = () => {
  // state
  const [currentToast, setCurrentToast] = useState<ToastQueueItem | null>(null);
  const [isToastOpen, setIsToastOpen] = useState(false);
  const [toastKey, setToastKey] = useState(0);

  // hooks
  const { isAuthenticated, isLoading } = useAuth();
  const [signOutAPI] = useSignOutMutation();
  const toastQueue = useSelector(getToastQueue);
  const account = useSelector(getAccount);
  const location = useLocation();
  const navigate = useNavigate();
  const { registerForPushNotifications, cleanup } = usePushNotification();
  const { mountIntercom, shutdown } = useIntercom();
  const { shutdown: shutdownLinkHandler } = useUniversalLinkHandler();

  // effects
  useEffect(() => {
    if (toastQueue.length > 0) {
      setCurrentToast(toastQueue[0]);
      setIsToastOpen(true);
      setToastKey((k) => k + 1);
    }
  }, [toastQueue]);

  useEffect(() => {
    if (!isToastOpen) {
      dispatch(removeToast());
    }
  }, [isToastOpen]);

  useEffect(() => {
    if (isAuthenticated || isLoading || !account) return;

    signOutAPI().catch((err) => {
      console.error(`Error signing out: `, err);
      dispatch(
        newToast({
          title: 'Error signing out',
          description: 'Please try again',
          variant: 'error',
        }),
      );
    });
  }, [account, isAuthenticated, isLoading, signOutAPI]);

  useEffect(() => {
    if (isLoading || !isAuthenticated) return;

    registerForPushNotifications().catch((err) => {
      console.error('Error registering for push notifications:', err);
    });

    return cleanup;
  }, [isLoading, isAuthenticated, registerForPushNotifications, cleanup]);

  useEffect(() => {
    if (
      isAuthenticated &&
      !isLoading &&
      account &&
      account.id &&
      account.accountRole?.name !== 'KareHero Administrator' &&
      account.accountRole?.name !== 'Organization Owner'
    ) {
      mountIntercom();
    }
    return () => shutdown();
  }, [isAuthenticated, isLoading, account, mountIntercom, shutdown]);

  const isOverrideBottom = useMemo(
    () =>
      location.pathname === ROUTES.REGISTER ||
      location.pathname === ROUTES.SIGN_IN,
    [location.pathname],
  );

  useEffect(() => {
    return shutdownLinkHandler;
  }, [shutdownLinkHandler]);

  return (
    <>
      {currentToast && (
        <Toast
          key={toastKey}
          title={currentToast?.title || ''}
          description={currentToast?.description}
          variant={currentToast?.variant}
          isOpen={isToastOpen}
          setIsOpen={setIsToastOpen}
          isOverrideBottom={isOverrideBottom}
          action={
            currentToast?.action
              ? {
                  label: currentToast.action.label,
                  onClick: () => {
                    setIsToastOpen(false);
                    currentToast.onAction?.();
                    navigate(currentToast.action!.href, {
                      state: { back: true },
                    });
                  },
                }
              : undefined
          }
        />
      )}
      <Outlet />
    </>
  );
};
